<template>
    <div>
        <!-- Sidebar -->
        <nav class="sidebar" :class="{ 'shadow-sm': viewingMap }">
            <div class="sidebar-sticky">
                <div class="accordion" id="accordion">
                    <div v-if="caseObj" class="nav">
                        <!-- Mobile Navbar Collapse Toggle -->
                        <li class="nav-item nav-toggle">
                            <a
                                class="nav-link parent active-link collapsed d-flex align-items-center py-2 pl-0"
                                data-toggle="collapse"
                                href="#sidebarCollapse"
                                role="button"
                                aria-expanded="false"
                                aria-controls="collapseExample"
                            >
                                <div class="align-middle h6 mb-0 flex-grow-1">&nbsp;{{ activeLinkTitle }}</div>
                                <div class="flex-grow-0 ml-1">
                                    <span class="icon icon-sm icon-glyphicons-basic-chevron-up caret"></span>
                                </div>
                            </a>
                        </li>

                        <!-- Side Nav -->
                        <div id="sidebarCollapse" class="collapse nav-content">
                            <div class="nav-section px-3 pt-4" style="padding-bottom: 24px">
                                <div class="pb-1 pt-1">
                                    <router-link :to="backToLibraryRoute">
                                        <span
                                            class="icon icon-sm icon-glyphicons-basic-chevron-left mr-1 align-middle"
                                        ></span>
                                        <span class="align-middle">Back to Library</span>
                                    </router-link>
                                </div>
                                <div class="mb-3">
                                    <span v-show="caseIsReadOnly" class="badge badge-light">
                                        <span class="icon icon-glyphicons-basic-lock"></span> Read Only View
                                    </span>
                                </div>
                                <span class="h6 mr-2 align-middle max-two-lines">{{ caseObj.title }}</span>
                                <span v-show="caseObj.community_published_status === 'in_review'" class="mr-1">
                                    <span class="badge badge-info align-middle">In Review</span>
                                </span>
                                <a
                                    v-if="caseObj.community_published_status == 'published'"
                                    class="badge badge-highlight case-badge mr-1 align-middle"
                                    :href="viewInCommunityLink"
                                    target="_blank"
                                >
                                    <span class="icon icon-glyphicons-basic-world-east align-middle mr-xs"></span>
                                    <span class="align-middle">Published</span>
                                </a>
                                <span v-if="caseObj.show_in_lms_deep_link">
                                    <info-popover
                                        content="This scenario is selectable from within your LMS"
                                        placement="right"
                                        justify="left"
                                        class="d-inline"
                                    >
                                        <span class="badge badge-light case-badge align-middle default-cursor">
                                            <span
                                                class="icon icon-glyphicons-basic-power-cord-plug align-middle mr-xs"
                                            ></span>
                                            <span class="align-middle">LMS</span>
                                        </span>
                                    </info-popover>
                                </span>
                                <span v-if="caseObj.content_license == 'cc-by-sa-4-0'" class="ml-3 mr-1">
                                    <creative-commons-icon
                                        height="16px"
                                        class="d-inline"
                                        placement="right"
                                    ></creative-commons-icon>
                                </span>
                                <div class="mt-2 pt-1">
                                    <p class="mb-0 text-secondary tiny">
                                        Last Modified: {{ lastModifiedDate }} by {{ lastModifiedByUser }}
                                    </p>
                                </div>
                            </div>

                            <!-- Settings -->
                            <div class="menu-item">
                                <div class="nav-item parent">
                                    <router-link
                                        :to="caseBaseUrl + 'settings'"
                                        @click.native="itemSelect()"
                                        active-class="active"
                                        :exact="false"
                                        class="nav-link"
                                    >
                                        <span
                                            class="icon icon-lg icon-glyphicons-basic-cogwheel mr-2 align-middle"
                                        ></span>
                                        <span class="align-middle">Settings</span>
                                    </router-link>
                                </div>
                            </div>

                            <!-- Share -->
                            <div class="menu-item">
                                <div class="nav-item parent">
                                    <router-link
                                        :to="caseBaseUrl + 'share'"
                                        @click.native="itemSelect()"
                                        :exact="false"
                                        active-class="active"
                                        class="nav-link"
                                        :class="{ disabled: casePreviewDisabled }"
                                    >
                                        <span class="icon icon-lg icon-glyphicons-basic-link mr-2 align-middle"></span>
                                        <span class="align-middle">Share</span>
                                    </router-link>
                                </div>
                            </div>

                            <!-- Timers -->
                            <div class="menu-item">
                                <div class="nav-item parent">
                                    <router-link
                                        :to="caseBaseUrl + 'timers'"
                                        @click.native="itemSelect()"
                                        active-class="active"
                                        :exact="true"
                                        class="nav-link"
                                    >
                                        <span
                                            class="icon icon-lg icon-glyphicons-basic-stopwatch mr-2"
                                            style="vertical-align: -5px"
                                        ></span>
                                        <span class="align-middle">Timers</span>
                                    </router-link>
                                </div>
                            </div>

                            <!-- Map -->
                            <div class="menu-item">
                                <div class="nav-item parent">
                                    <router-link
                                        id="sidenav-map-link"
                                        :to="caseBaseUrl + 'map'"
                                        @click.native="itemSelect()"
                                        active-class="active"
                                        class="nav-link"
                                        :class="{ disabled: casePreviewDisabled }"
                                    >
                                        <span
                                            class="icon icon-lg icon-glyphicons-basic-cluster mr-2 align-middle"
                                        ></span>
                                        <span class="align-middle">Map</span>
                                    </router-link>
                                </div>
                            </div>

                            <!-- Launch Preview -->
                            <div class="menu-item" style="padding-bottom: 20px">
                                <div class="nav-item parent">
                                    <a
                                        @click="launchPreview()"
                                        class="nav-link"
                                        :class="{ disabled: casePreviewDisabled }"
                                    >
                                        <span
                                            class="icon icon-lg icon-glyphicons-basic-video-play-empty mr-2 align-middle"
                                        ></span>
                                        <span class="align-middle">Preview</span>
                                    </a>
                                </div>
                            </div>

                            <!-- Nodes -->
                            <h6 class="d-flex justify-content-between align-items-center px-3 mt-3 mb-2 pb-1">
                                <span class="align-middle sidebar-heading">Screens</span>
                                <a
                                    @click="openCreateNode()"
                                    v-show="!caseIsReadOnly"
                                    class="badge badge-light align-middle mt-0"
                                >
                                    <span class="icon icon-glyphicons-basic-plus mr-1"></span>
                                    <span>New</span>
                                </a>
                            </h6>

                            <div class="node-search mb-2">
                                <input
                                    v-model="search"
                                    type="text"
                                    class="form-control form-control-sm"
                                    id="searchNodes"
                                    placeholder="Search screens..."
                                    autocomplete="off"
                                />
                            </div>

                            <div class="d-flex">
                                <!-- Node Link Diagram -->
                                <div class="flex-grow-0">
                                    <div id="svg-container" class="svg-container d-flex flex-column">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            id="svg-parent"
                                            class="svg-parent flex-grow-1"
                                            v-if="selectedNode.id && search == '' && !loading && viewingNodeDetail"
                                        >
                                            <polyline
                                                :points="lineArrayFormatted"
                                                class="svg-line"
                                                stroke-linecap="round"
                                            />
                                            <polyline
                                                :points="lineArrayFormatted2"
                                                class="svg-line"
                                                stroke-linecap="round"
                                            />
                                            <circle
                                                v-for="(linkedNode, index) in linkedNodeList"
                                                v-show="linkedNode.yPosition"
                                                :key="index"
                                                :cx="diagramLineXPosition"
                                                :cy="linkedNode.yPosition"
                                                r="3.25"
                                                :class="{
                                                    'next-node-circle': linkedNode.nodeType == 'next',
                                                    'previous-node-circle': linkedNode.nodeType == 'previous',
                                                }"
                                            />
                                        </svg>
                                    </div>
                                </div>

                                <!-- Node List -->
                                <div class="flex-grow-1 node-list-container pb-lg-5">
                                    <node-link-list
                                        :nodes="nodesFiltered"
                                        :case-base-url="caseBaseUrl"
                                        v-on:updated="generateNodeLinkDiagram()"
                                        v-on:update-order="updateNodeOrder($event)"
                                        :locked="caseLocked || search !== ''"
                                    ></node-link-list>
                                    <div class="create-node-link">
                                        <a
                                            class="small text-secondary"
                                            @click="openCreateNode()"
                                            v-show="!caseIsReadOnly"
                                            >Add Screen...</a
                                        >
                                    </div>
                                    <div
                                        v-show="!loading && !caseObj.node_auto_numbering_enabled"
                                        class="mt-4 pl-2 text-secondary tiny"
                                    >
                                        <span class="icon icon-glyphicons-basic-switch-off mr-1"></span>
                                        <span
                                            >Screen auto-numbering is
                                            <router-link :to="caseBaseUrl + 'settings'">disabled</router-link></span
                                        >
                                    </div>
                                </div>
                            </div>

                            <div v-if="nodes.length == 0 && !loading">
                                <div class="d-flex justify-content-center mb-3 mt-5">
                                    <span class="px-2 small text-secondary"
                                        >This scenario doesn't have any screens yet.</span
                                    >
                                </div>
                                <div class="d-flex justify-content-center">
                                    <button class="btn btn-sm btn-primary" @click="openCreateNode()">
                                        Create a Screen
                                    </button>
                                </div>
                            </div>

                            <br />
                        </div>
                    </div>
                </div>
            </div>
        </nav>

        <!-- Nested Detail View -->
        <div class="sidenav-view">
            <div class="d-flex">
                <div class="flex-grow-0">
                    <div class="sidenav-spacer"></div>
                </div>
                <div class="flex-grow-1">
                    <router-view v-on:update-node-order="updateNodeOrder(null)"></router-view>
                </div>
            </div>
        </div>

        <!-- Create Node Modal -->
        <base-modal id="create-modal" :loading="modalLoading" :centered="true">
            <template slot="title"> New Screen </template>
            <template>
                <base-form
                    class="mt-3"
                    v-model="newNode"
                    :fields="fieldsConfig"
                    :inline-style="true"
                    ref="createNodeForm"
                ></base-form>
            </template>
            <template slot="footer">
                <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
                <button
                    @click="createNode()"
                    :disabled="caseLocked || modalLoading"
                    class="btn btn-primary"
                    type="button"
                >
                    Save
                </button>
            </template>
        </base-modal>

        <!-- First Creation Modal -->
        <base-modal id="first-create-modal" :centered="true">
            <template>
                <div class="d-flex justify-content-center py-4">
                    <a href="https://vimeo.com/891490281/16a981813f?share=copy" rel="noopener" target="_blank">
                        <img
                            src="/static/general/img/thumbnails/create.png"
                            height="150"
                            class="rounded-lg shadow-sm obj-cover"
                            alt=""
                        />
                    </a>
                </div>
                <div class="mb-3 pt-3">
                    <div>
                        <p class="text-center mb-3 h5 font-weight-bold">New to authoring in Affinity?</p>
                        <p class="text-center mb-3 text-secondary">Watch a quick video to help you get started.</p>
                    </div>
                    <div class="d-flex justify-content-center pt-3 pb-4">
                        <button class="btn btn-link mr-1" type="button" data-dismiss="modal">Skip Video</button>
                        <a
                            class="btn btn-primary"
                            type="button"
                            href="https://vimeo.com/891490281/16a981813f?share=copy"
                            rel="noopener"
                            target="_blank"
                            ><span class="mr-1 align-middle">Watch a 3-minute walkthrough</span>
                            <span class="icon icon-100 icon-glyphicons-basic-square-new-window align-middle"></span
                        ></a>
                    </div>
                </div>
                <div>
                    <support-request-prompt class="text-center" />
                </div>
            </template>
        </base-modal>
    </div>
</template>

<script>
import http from "@/axios.js"
import BaseModal from "@/components/BaseModal.vue"
import BaseForm from "@/components/BaseForm"
import NodeLinkList from "@/components/NodeLinkList"
import Utilities from "@/assets/js/utilities.js"
import CreativeCommonsIcon from "@/components/CreativeCommonsIcon"
import SupportRequestPrompt from "@/components/SupportRequestPrompt"
import InfoPopover from "@/components/InfoPopover"

export default {
    name: "CaseDetail",
    components: {
        BaseModal,
        BaseForm,
        NodeLinkList,
        CreativeCommonsIcon,
        SupportRequestPrompt,
        InfoPopover,
    },
    data() {
        return {
            baseUrl: process.env.BASE_URL,
            search: "",
            newNode: {},
            modalLoading: false,
            // The x position of the node link diagram.
            diagramLineXPosition: 10,
            // Controls the width of the notch in the node link diagram.
            diagramNotchVertical: 9,
            // Controls the depth of the notch in the node link diagram.
            diagramNotchDepth: 7,
            linkedNodeList: [],
            lineArrayFormatted: "",
            lineArrayFormatted2: "",
            previewLoading: false,
        }
    },
    computed: {
        loading() {
            return this.$store.getters.loading
        },
        casePreviewDisabled() {
            return !this.nodes || this.nodes.length == 0 || !this.caseObj.id || this.previewLoading
        },
        caseIsReadOnly() {
            return this.$store.getters.caseIsReadOnly
        },
        activeLink() {
            return this.$route.name
        },
        activeLinkTitle() {
            return this.$route.meta.title
        },
        caseObj() {
            return this.$store.getters.case
        },
        caseLocked() {
            // Leave all cases unlocked for now.
            return false
        },
        nodes() {
            return this.$store.getters.case.nodes
        },
        caseBaseUrl() {
            return `/library/${this.$route.params.id}/`
        },
        backToLibraryRoute() {
            let route = {
                name: "OrganizationLibrary",
            }
            if (this.$store.getters.libraryFolderId) {
                route.query = {
                    folder: this.$store.getters.libraryFolderId,
                }
            }
            return route
        },
        nodesFiltered() {
            let vm = this
            return this.nodes.filter(
                (node) =>
                    node.title.toLowerCase().includes(vm.search.toLowerCase()) ||
                    node.number.toString().includes(vm.search)
            )
        },
        lastModifiedDate() {
            return Utilities.formatDateTime(this.caseObj.last_modified)
        },
        lastModifiedByUser() {
            if (this.caseObj.last_modified_by) return this.caseObj.last_modified_by.email
            else return ""
        },
        // The current node being viewed.
        selectedNode() {
            return this.$store.getters.selectedNode
        },
        currentRouteName() {
            return this.$route.name
        },
        viewingNodeDetail() {
            return this.currentRouteName === "NodeDetail"
        },
        viewingMap() {
            return this.currentRouteName === "CaseNodeMap"
        },
        caseID() {
            return this.$route.params.id
        },
        fieldsConfig() {
            let fields = [
                {
                    name: "Title",
                    key: "title",
                    required: true,
                    placeholder: "Enter a title for the new screen",
                },
            ]
            return fields
        },
        // Returns a link to the community view for a published case.
        viewInCommunityLink() {
            return this.$router.resolve({ name: "CommunityCaseDetail", params: { id: this.caseObj.id } }).href
        },
    },
    watch: {
        caseID: async function (newVal, oldVal) {
            // TODO: Improve this. This triggers a hard refresh of the entire window when
            // the user selects a different case in the global searchbar while already viewing
            // the CaseDetail view.
            if (newVal && oldVal && String(newVal) != String(oldVal)) {
                location.reload()
            }
        },
        // Regenerate the node link diagram when a new node is selected.
        selectedNode: {
            handler: function () {
                this.generateNodeLinkDiagram()
            },
            deep: true,
        },
        // Regenerate the node link diagram when the search bar value changes,
        // which causes the node list to re-render.
        search: function () {
            if (this.search != "") {
                this.generateNodeLinkDiagram()
            }
        },
    },
    methods: {
        // Fetch data for the view.
        fetchData() {
            this.$store.dispatch("addLoad")
            let vm = this
            this.$store.dispatch("fetchCase", this.caseID).then(function () {
                vm.$store.dispatch("removeLoad")
                if (vm.selectedNode.id) {
                    vm.scrollToSelectedNode()
                    vm.generateNodeLinkDiagram()
                }
            })
        },
        // Scroll to the currently selected node.
        scrollToSelectedNode() {
            let vm = this
            vm.$nextTick(function () {
                let nodeElement = document.getElementById("listnode" + vm.selectedNode.id)
                try {
                    nodeElement.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    })
                } catch (e) {
                    console.log(e)
                }
            })
        },
        // Open the create node modal.
        openCreateNode() {
            this.$refs.createNodeForm.resetValidate()
            // Initialize the node object for creation.
            this.newNode = {}
            $("#create-modal").modal("show")
        },
        // Create a new node.
        createNode() {
            // Check form validity.
            if (!this.$refs.createNodeForm.validate()) {
                return
            }
            this.modalLoading = true
            let vm = this
            http.post("/api/cases/" + this.$route.params.id + "/nodes/", this.newNode)
                .then(function (response) {
                    $("#create-modal").modal("hide")
                    vm.$notify({
                        group: "main",
                        type: "success",
                        text: "Screen created.",
                        data: {},
                    })
                    vm.$store.dispatch("refreshCase", vm.caseID)
                    vm.$store.commit("mapRequiresUpdate")
                    vm.$router.push(vm.caseBaseUrl + "nodes/" + response.data.id + "/")
                    vm.$nextTick(function () {
                        vm.scrollToSelectedNode()
                    })
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        // Start a new case preview session.
        launchPreview() {
            // Using a separate flag here to ensure that a preview isn't double clicked,
            // as this causes the first preview session to be invalidated.
            this.previewLoading = true
            this.$store.dispatch("addLoad")
            this.$store.dispatch("clearSession")
            let vm = this
            let previewSessionStartURL = "/api/cases/" + this.caseObj.id + "/preview/start/"
            http.post(previewSessionStartURL)
                .then(function (response) {
                    vm.$store.dispatch("removeLoad")
                    vm.$router.push(`${vm.caseBaseUrl}preview/${response.data.session_id}/`)
                })
                .finally(function () {
                    vm.previewLoading = false
                })
        },
        // Collapse the sidenav on small screens when an item is selected.
        itemSelect() {
            if (window.innerWidth < 1000) {
                $("#sidebarCollapse").collapse("hide")
            }
        },
        // Return the y position of a node in the menu, relative to the svg container.
        nodeYPosition(node) {
            let nodeElement = document.getElementById("listnode" + node.id)
            let svgContainerElement = document.getElementById("svg-container")

            // If either elements arent rendered yet, return null.
            if (!nodeElement || !svgContainerElement) {
                return null
            }

            // Calculate y position of the given node, relative to the svg container.
            let svgContainerOffsetTop = svgContainerElement.offsetTop
            let selectedNodeOffsetTop = nodeElement.offsetTop
            let selectedNodeTopPostionRelative = selectedNodeOffsetTop - svgContainerOffsetTop

            // Add half the height of the node element, to get the y position to the vertical
            // middle of the node.
            let selectedNodeMiddlePostionRelative = selectedNodeTopPostionRelative + nodeElement.offsetHeight / 2
            return selectedNodeMiddlePostionRelative
        },
        // Formats a set of x,y coordintates into a polyline-friendly string.
        formatPolylinePoints(pointsArray) {
            let pointString = ""
            pointsArray.forEach((coord) => {
                pointString += coord.join(",") + " "
            })
            return pointString
        },
        // Generate the node link diagram.
        generateNodeLinkDiagram() {
            this.generateNodeLinkList()
            this.generateNodeLinkLines()
        },
        // Generate the list of linked nodes, with their vertical position attached.
        // - can't use a computed property here as the resulting SVG depends on the
        // DOM being rendered.
        generateNodeLinkList() {
            if (!this.selectedNode.id) {
                this.linkedNodeList = []
                return
            }

            let vm = this
            let linkedNodeList = []

            // Add all 'previous' nodes, label them and fetch their vertical positions.
            this.selectedNode.previous_nodes.forEach((node) => {
                linkedNodeList.push({
                    nodeType: "previous",
                    node: node,
                    yPosition: vm.nodeYPosition(node),
                })
            })

            // Add all 'next' nodes, label them and fetch their vertical positions.
            this.selectedNode.next_nodes.forEach((node) => {
                linkedNodeList.push({
                    nodeType: "next",
                    node: node,
                    yPosition: vm.nodeYPosition(node),
                })
            })
            this.linkedNodeList = linkedNodeList
        },
        // Generate the connecting lines in the node link diagram.
        // - can't use a computed property here as the resulting SVG depends on the
        // DOM being rendered.
        generateNodeLinkLines() {
            if (!this.linkedNodeList) {
                return
            }

            let selectedNodeYPosition = this.nodeYPosition(this.selectedNode)

            if (!selectedNodeYPosition) {
                return
            }

            // The highest positioned node (vertically) in the list of linked nodes.
            let maxLinkedNodeYPosition = Math.max(...this.linkedNodeList.map((node) => node.yPosition))
            let minLinkedNodeYPosition = Math.min(...this.linkedNodeList.map((node) => node.yPosition))

            let pointArray = []
            let pointArray2 = []
            // Only add a line above if there are linked nodes above the selected one.
            if (minLinkedNodeYPosition < selectedNodeYPosition) {
                pointArray.push(
                    [this.diagramLineXPosition, minLinkedNodeYPosition],
                    [this.diagramLineXPosition, selectedNodeYPosition - this.diagramNotchVertical],
                    [this.diagramLineXPosition + this.diagramNotchDepth, selectedNodeYPosition - 2]
                )
            }
            // Only add a line below if there are linked nodes below the selected one.
            if (maxLinkedNodeYPosition > selectedNodeYPosition) {
                pointArray2.push(
                    [this.diagramLineXPosition + this.diagramNotchDepth, selectedNodeYPosition + 2],
                    [this.diagramLineXPosition, selectedNodeYPosition + this.diagramNotchVertical],
                    [this.diagramLineXPosition, maxLinkedNodeYPosition]
                )
            }
            this.lineArrayFormatted = this.formatPolylinePoints(pointArray)
            this.lineArrayFormatted2 = this.formatPolylinePoints(pointArray2)
        },
        // Re-order and re-number the nodes.
        updateNodeOrder(nodeList) {
            if (!nodeList) {
                nodeList = this.nodes
            }

            this.generateNodeLinkDiagram()

            let nodesFormatted = []
            let newNodeList = []

            // Only auto-generate node numbers if enabled on the case.
            if (this.caseObj.node_auto_numbering_enabled) {
                newNodeList = []
                for (let index = 0; index < nodeList.length; index++) {
                    let node = JSON.parse(JSON.stringify(nodeList[index]))
                    node.number = index + 1
                    // Need to update the label too, as it contains the node number as a string.
                    node.label = `${node.number}. ${node.title}`
                    newNodeList.push(node)
                }
            } else {
                newNodeList = nodeList
            }

            nodesFormatted = newNodeList.map((n) => {
                return { id: n.id, number: n.number }
            })

            // Persist the change in the datastore.
            this.$store.commit("setNodeList", newNodeList)

            let vm = this
            http.put(`/api/cases/${this.caseID}/nodes/numbers/`, {
                nodes: nodesFormatted,
            }).then(async function () {
                // Make sure the map gets reloaded when its viewed next - screen numbers
                // have changed.
                vm.$store.commit("mapRequiresUpdate")
                if (vm.currentRouteName == "CaseNodeMap") {
                    // Refresh the map if it's being viewed.
                    vm.$store.dispatch("addLoad")
                    await vm.$store.dispatch("refreshCaseNodeMap")
                    vm.$store.dispatch("removeLoad")
                }
            })
        },
    },
    destroyed() {
        // // Clear case data from store.
        // this.$store.dispatch("clearCase")
    },
    created() {
        this.fetchData()
    },
    beforeMount() {
        let storedCaseID = this.$store.getters.case.id
        // Clear the currently loaded case before mounting the component if
        // viewing a new case - this prevents old data from being shown.
        if (this.$route.params.id != storedCaseID) {
            this.$store.dispatch("clearCase")
        }
        // Clear the selected node in the datastore if not visiting/return to the node
        // detail view.
        if (!this.viewingNodeDetail) {
            this.$store.dispatch("clearSelectedNode")
        }
    },
    async mounted() {
        await this.$store.dispatch("fetchAuth")
        if (!this.$store.getters.user.viewed_tours.first_create) {
            // Show the modal.
            $("#first-create-modal").modal("show")
            // Track that the user has been shown the tour, to avoid showing it twice.
            http.put("/api/tour-viewed/", { tourId: "first_create" })
        }
    },
}
</script>

<style scoped lang="scss">
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/mixins";

$sidenav-link: $gray-700;
$sidenav-background: #fff;
$sidenav-background-active: #f1f4f7;
$sidenav-nested-background: $gray-200;
$sidenav-font-header: 0.8rem;
$sidenav-font-parent: 14px;
$sidenav-font-child: 0.875rem;
$sidenav-btn-radius: $border-radius-sm;

// Side Nav
.sidenav-view {
    background-color: inherit;
    color: #313131;
    margin-left: 0px;
    padding-left: 0px;
    padding-right: 0px;
    padding-top: 15px;
}

.sidenav-spacer {
    @media (min-width: $sidenav-collapse) {
        width: $sidenav-width;
    }
}

.child-spacer {
    height: 10px;
    width: 10px;
}

.sidebar {
    background-color: $sidenav-background;
    top: 0;
    width: 100%;
    bottom: 0;
    left: 0;
    padding-top: 0px;
    height: fit-content;

    @media (min-width: $sidenav-collapse) {
        position: fixed;
        width: $sidenav-width;
        border-right: 1px solid $gray-300;
        // Set padding top to the height of the navbar.
        padding: $navbar-spacer 0 0;
    }

    // Set sidebar to behind the topnav.
    z-index: 100;
}

.sidebar-fixed {
    position: fixed;
}

.nav-content {
    width: 100%;

    @media (min-width: $sidenav-collapse) {
        display: initial !important;
    }
}

.nav-section {
    width: 100%;
}

.menu-item {
    width: 100%;
}

.nav-toggle {
    @media (min-width: $sidenav-collapse) {
        display: none;
    }

    a {
        font-size: 1.25rem;
        font-weight: $font-weight-light;
    }
}

.sidebar-sticky {
    height: auto;

    @media (min-width: $sidenav-collapse) {
        position: relative;
        top: 0;
        height: 100vh;
        overflow-x: hidden;
        // Scrollable contents if viewport is shorter than content.
        overflow-y: auto;
    }
}

.sidebar-heading {
    font-size: 0.75rem;
    text-transform: uppercase;
    color: $gray-600;
}

.nav-item.parent {
    padding-right: 0px;

    @media (min-width: $sidenav-collapse) {
        padding-right: 1px;
    }
}

.nav-link {
    color: $sidenav-link;
    padding: 0.25rem 0.35rem;
    font-size: $sidenav-font-parent;
    font-weight: 500;
    margin-left: 16px;
    margin-right: 16px;
    border-radius: $sidenav-btn-radius !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.active {
        color: $primary;

        @media (min-width: $sidenav-collapse) {
            background-color: $sidenav-background-active !important;
        }
    }

    &:hover {
        background-color: $sidenav-background-active;
    }

    &.disabled {
        color: $gray-500;
        pointer-events: none;
    }

    &.parent {
        &.collapsed {
            .caret {
                transform: rotate(180deg);
            }
        }
        .caret {
            transform: initial;
        }
    }

    &.child {
        padding-left: 44px;
        font-size: $sidenav-font-child;
        font-weight: normal;

        &.disabled {
            color: $gray-600;
            pointer-events: none;
        }

        &:hover {
            background-color: $gray-300;
        }
    }
}

ul {
    width: 100%;
}

li {
    width: 100%;
}

.nav {
    &.nested {
        background-color: $sidenav-nested-background;
    }
}

.badge {
    margin-bottom: 2px;
    margin-top: 2px;
    font-size: 10px;
}

a.navbar-brand {
    font-weight: 400;
    color: inherit;
}

.tool-btn {
    cursor: pointer;
    color: $gray-700;
    padding-left: 6px;
    padding-bottom: 10px;
    padding-right: 8px;
    padding-top: 8px;
    border-radius: $sidenav-btn-radius;
    margin-left: -6px;

    &:hover {
        background-color: $gray-200;
    }
}

.footer-section {
    @media (min-width: $sidenav-collapse) {
        position: absolute;
        bottom: 30px;
    }
}

.footer-header {
    @media (min-width: $sidenav-collapse) {
        display: none;
    }
}

.profile-dropdown {
    cursor: pointer;
    font-size: 26px;

    @media (max-width: ($sidenav-collapse - 1)) {
        margin-left: 6px;
    }
}

.active-link {
    background-color: inherit !important;
}

.node-search {
    margin: 0.25rem 1rem;
}

.node-list-container {
    overflow: hidden;
    max-width: 100%;
}

.svg-container {
    height: 100%;
    width: 17px;
}

.svg-parent {
    height: 100%;
    width: 100%;
}

.svg-line {
    fill: none;
    stroke: $node-connector-color;
    stroke-width: 1.2px;
}

.next-node-circle {
    fill: $primary;
}

.previous-node-circle {
    fill: $gray-550;
}

.create-node-link {
    padding-left: 6px;
    margin-top: 3px;
}
</style>